/**
 * Komal OS - Quality Store
 * Manages checklists and inspections.
 */

import { create } from 'zustand';
import * as qualityService from '../services/qualityService';

const useQualityStore = create((set, get) => ({
  checklists: [],
  inspections: [],
  selectedInspection: null,
  isLoading: false,
  isSubmitting: false,
  error: null,

  clearError: () => set({ error: null }),

  fetchChecklists: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await qualityService.getChecklists(params);
      set({ checklists: response.data.checklists || [], isLoading: false });
      return response.data.checklists;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load checklists';
      set({ error: message, isLoading: false });
      return [];
    }
  },

  createChecklist: async (data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await qualityService.createChecklist(data);
      await get().fetchChecklists();
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create checklist';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  cloneChecklistToProject: async (id, data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await qualityService.cloneChecklistToProject(id, data);
      await get().fetchChecklists();
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to clone checklist';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  fetchInspections: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await qualityService.getInspections(params);
      set({ inspections: response.data.inspections || [], isLoading: false });
      return response.data.inspections;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load inspections';
      set({ error: message, isLoading: false });
      return [];
    }
  },

  fetchInspection: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await qualityService.getInspection(id);
      set({ selectedInspection: response.data.inspection, isLoading: false });
      return response.data.inspection;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load inspection';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  createInspection: async (data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await qualityService.createInspection(data);
      await get().fetchInspections();
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create inspection';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  signOffInspection: async (id, data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await qualityService.signOffInspection(id, data);
      await get().fetchInspections();
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to sign off inspection';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  // Records or corrects the inspector's result (pending/passed/failed) on an
  // already-created inspection (e.g. one created via Production Plan's
  // "Call Inspection", which always starts "pending") - separate from Sign
  // Off, which only works once the result here is "passed". Also how a
  // wrongly recorded result gets recalled (status: 'pending').
  setInspectionResult: async (id, resultStatus, findings) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await qualityService.setInspectionResult(id, resultStatus, findings);
      await get().fetchInspections();
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update inspection result';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  // Corrects informational fields only (findings/comments/type/inspector) -
  // e.g. fixing wrong information entered when the inspection was recorded.
  editInspection: async (id, data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await qualityService.updateInspection(id, data);
      await get().fetchInspections();
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update inspection';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },
}));

export default useQualityStore;
